/**
 * Firebase schema utilities for Virtex.
 * Defines strong types and lightweight runtime validation for Firebase nodes.
 * No external dependencies required.
 */

// ==================== Schema classes (edit here) ====================
// Lightweight decorators and class definitions used to generate the runtime schema.
// Allowed sync scopes for ModalHandler
export type SyncScope = 'global' | 'local';


// Internal registry for class metadata
type FieldRuntimeSpec =
  | string
  | { '[]': FieldRuntimeSpec }
  | { $ref: Function }
  | { $unionVariants: { base: Function; variants: Record<string, Function> } }
  | { [key: string]: FieldRuntimeSpec };

interface ClassSchemaMeta {
  name: string;
  fields: Record<string, FieldRuntimeSpec>;
}

const __schemaRegistry = new Map<Function, ClassSchemaMeta>();

function ensureClassMeta(ctor: Function, name?: string): ClassSchemaMeta {
  let meta = __schemaRegistry.get(ctor);
  if (!meta) {
    meta = { name: name || ctor.name, fields: {} };
    __schemaRegistry.set(ctor, meta);
  } else if (name && !meta.name) {
    meta.name = name;
  }
  return meta;
}

function SchemaClass(name?: string) {
  return function (ctor: Function) {
    ensureClassMeta(ctor, name);
  };
}

function Field(spec: FieldRuntimeSpec) {
  return function (target: any, propertyKey: string) {
    const meta = ensureClassMeta(target.constructor);
    meta.fields[propertyKey] = spec;
  };
}

function Ref(cls: Function) {
  return Field({ $ref: cls });
}

function ArrayOf(spec: string | Function | { $ref: Function } | FieldRuntimeSpec) {
  const inner: FieldRuntimeSpec = typeof spec === 'function' ? { $ref: spec } : (spec as any);
  return Field({ '[]': inner });
}

function ArrayOfUnion(base: Function, variants: Record<string, Function>) {
  // Represent as an array where the element schema is the union under BaseInteractivity
  return Field({ '[]': { $unionVariants: { base, variants } } });
}

// ---------- Classes for schema generation ----------

@SchemaClass('ComponentModel')
class ComponentModel {
  @Field('boolean') enabled!: boolean;
  @Field('string') file!: string;
  @Field('string') id!: string;
  @Field('string') name!: string;
  @Field('number') posX!: number; @Field('number') posY!: number; @Field('number') posZ!: number;
  @Field('number') rotX!: number; @Field('number') rotY!: number; @Field('number') rotZ!: number;
  @Field('number') scale!: number;
  @Field('"ComponentModel"') type!: 'ComponentModel';
  @Field('boolean?') isPickable?: boolean;
  @Field('boolean?') checkCollisions?: boolean;
}

@SchemaClass('GroundConfig')
class GroundConfig {
  @Field('string') fileName!: string;
  @Field('number') height!: number;
  @Field('string|number?') maxHeight?: string | number;
  @Field('number') subdivisions!: number;
  @Field('string?') terrain?: string;
  @Field('number') width!: number;
}

@SchemaClass('SkyboxConfig')
class SkyboxConfig {
  @Field('number?') posy?: number;
  @Field('string?') reflectionTexture?: string;
  @Field('number?') size?: number;
}

@SchemaClass('LessonPlanUI')
class LessonPlanUI {
  @Field('string?') buttonText?: string;
  @Field('string?') interactivityClass?: string;
  @Field('string?') modalId?: string;
  @Field('string?') type?: string;
}

@SchemaClass('LessonPlanStep')
class LessonPlanStep {
  @Ref(LessonPlanUI) UI?: LessonPlanUI;
  @Field('string?') description?: string;
  @Field('string') id!: string;
  @Field('string?') name?: string;
  @Field('number?') seqNo?: number;
}

@SchemaClass('ModifierItem')
class ModifierItem {
  @Field('string') class!: string;
  @Field('string?') commands?: string;
  @Field('string?') componentname?: string;
  @Field('boolean?') enabled?: boolean;
  @Field('number?') seqNo?: number;
}

@SchemaClass('BaseInteractivity')
class BaseInteractivity {
  @Field('string') class!: string;
  @Field('string?') component?: string;
  @Field('boolean?') enabled?: boolean;
  @Field('string?') name?: string;
  @Field('number|string?') seqNo?: number | string;
  @Field('string?') type?: string;
}

@SchemaClass('Grass')
class GrassInteractivity extends BaseInteractivity {
  @Field({ '[]': { nBlades: 'number', radius: 'number', xPos: 'number', zPos: 'number' } })
  patches!: any;
}

@SchemaClass('ModalHandler')
class ModalHandlerInteractivity extends BaseInteractivity {
  @Field('string') modalHTMLFileName!: string;
  @Field('string') modalId!: string;
  @Field('"global"|"local"') syncScope!: 'global' | 'local';
}

@SchemaClass('Fire')
class FireInteractivity extends BaseInteractivity {
  @Field('number') posX!: number;
  @Field('number') posY!: number;
  @Field('number') posZ!: number;
}

@SchemaClass('Tracker')
class TrackerInteractivity extends BaseInteractivity {}

@SchemaClass('WindDirection')
class WindDirectionInteractivity extends BaseInteractivity {}

@SchemaClass('Scenario')
class Scenario {
  @Field('string?') cameraConfigName?: string;
  @ArrayOf(ComponentModel) components!: ComponentModel[];
  @Field('string?') description?: string;
  @Field('string?') displayName?: string;
  @Field('boolean?') doPerformanceLogging?: boolean;
  @Ref(GroundConfig) ground?: GroundConfig;
  @Field('string') id!: string;
  @ArrayOfUnion(BaseInteractivity, {
    Fire: FireInteractivity,
    Grass: GrassInteractivity,
    ModalHandler: ModalHandlerInteractivity,
    Tracker: TrackerInteractivity,
    WindDirection: WindDirectionInteractivity,
  })
  interactivities?: any[];
  @Field('boolean?') isVersion2?: boolean;
  @ArrayOf(LessonPlanStep) lessonplan?: LessonPlanStep[];
  @ArrayOf(ModifierItem) modifiers?: ModifierItem[];
  @Field('boolean?') showAxis?: boolean;
  @Ref(SkyboxConfig) skybox?: SkyboxConfig;
  @Field('number?') stagingX0?: number; @Field('number?') stagingX1?: number; @Field('number?') stagingY0?: number; @Field('number?') stagingY1?: number;
}

// Strong type for a ModalHandler interactivity node
// Note: We now use the class name 'ModalHandlerInteractivity' as both runtime and type

type ValidationOk<T> = { ok: true; value: T };
type ValidationErr = { ok: false; errors: string[] };
export type ValidationResult<T> = ValidationOk<T> | ValidationErr;

export class FirebaseSchema {
  // Default object for a ModalHandler interactivity
  static readonly modalHandlerDefaults: ModalHandlerInteractivity = {
    class: 'ModalHandler',
    component: '',
    enabled: true,
    modalHTMLFileName: 'inviteWithUrl.html',
    modalId: 'inviteWithUrlModalId',
    name: 'testia',
    syncScope: 'local',
  };

  /** Type guard for ModalHandlerInteractivity */
  static isModalHandler(x: any): x is ModalHandlerInteractivity {
    return (
      x &&
      x.class === 'ModalHandler' &&
      typeof x.component === 'string' &&
      typeof x.enabled === 'boolean' &&
      typeof x.modalHTMLFileName === 'string' &&
      typeof x.modalId === 'string' &&
      typeof x.name === 'string' &&
      (x.syncScope === 'global' || x.syncScope === 'local')
    );
  }

  /**
   * Validate an unknown value as ModalHandlerInteractivity.
   * Returns detailed errors on mismatch.
   */
  static validateModalHandler(x: any): ValidationResult<ModalHandlerInteractivity> {
    const errors: string[] = [];
    if (!x || typeof x !== 'object') {
      return { ok: false, errors: ['Value is not an object'] };
    }
    if (x.class !== 'ModalHandler') errors.push("class must be 'ModalHandler'");
    if (typeof x.component !== 'string') errors.push('component must be string');
    if (typeof x.enabled !== 'boolean') errors.push('enabled must be boolean');
    if (typeof x.modalHTMLFileName !== 'string') errors.push('modalHTMLFileName must be string');
    if (typeof x.modalId !== 'string') errors.push('modalId must be string');
    if (typeof x.name !== 'string') errors.push('name must be string');
    if (x.syncScope !== 'global' && x.syncScope !== 'local') errors.push("syncScope must be 'global' or 'local'");

    if (errors.length) return { ok: false, errors };
    return { ok: true, value: x as ModalHandlerInteractivity };
  }

  /**
   * Coerce a partial/unknown object to a normalized ModalHandlerInteractivity
   * by applying defaults and converting basic types.
   */
  static coerceModalHandler(x: any): ModalHandlerInteractivity {
    const def = FirebaseSchema.modalHandlerDefaults;
    const out: ModalHandlerInteractivity = {
      class: 'ModalHandler',
      component: asString(x?.component, def.component),
      enabled: asBoolean(x?.enabled, def.enabled),
      modalHTMLFileName: asString(x?.modalHTMLFileName, def.modalHTMLFileName),
      modalId: asString(x?.modalId, def.modalId),
      name: asString(x?.name, def.name),
      syncScope: asSyncScope(x?.syncScope, def.syncScope),
    };
    return out;
  }

  /** Pretty-print helper */
  static pretty(obj: unknown): string {
    try { return JSON.stringify(obj, null, 2); } catch { return String(obj); }
  }

  /** Validate a value against the class-generated Scenario schema. */
  static validateScenario(x: any): ValidationResult<Scenario> {
    const schema = FirebaseSchemaBuilder.buildScenarioTypeSchemaFromClasses();
    const errors = FirebaseSchema.validateAgainstSchema(x, schema, 'Scenario');
    return errors.length ? { ok: false, errors } : { ok: true, value: x as Scenario };
  }

  /** Generic validator for our simple schema DSL (strings with optional '?', unions via '|', literals in quotes, arrays via { '[]': schema }, objects via nested maps). */
  static validateAgainstSchema(value: any, schema: any, path = ''): string[] {
    // Special-case: if schema is the interactivities union shape, but value is an array
    if (
      schema &&
      typeof schema === 'object' &&
      !Array.isArray(schema) &&
      'BaseInteractivity' in schema &&
      Array.isArray(value)
    ) {
      return FirebaseSchema.validateInteractivitiesArray(value, schema.BaseInteractivity, path);
    }

    // Primitive spec string
    if (typeof schema === 'string') {
      return FirebaseSchema.validatePrimitive(value, schema, path);
    }

    // Array spec
    if (schema && typeof schema === 'object' && '[]' in schema) {
      if (!Array.isArray(value)) {
        return [`${path}: expected array`];
      }
      const elemSchema = (schema as any)['[]'];
      let errs: string[] = [];
      for (let i = 0; i < value.length; i++) {
        errs = errs.concat(
          FirebaseSchema.validateAgainstSchema(value[i], elemSchema, `${path}[${i}]`)
        );
      }
      return errs;
    }

    // Object spec
    if (schema && typeof schema === 'object') {
      if (value === null || typeof value !== 'object' || Array.isArray(value)) {
        return [`${path}: expected object`];
      }
      let errs: string[] = [];
      for (const key of Object.keys(schema)) {
        const childSpec: any = schema[key];
        const isStringSpec = typeof childSpec === 'string';
        const optional = isStringSpec && childSpec.endsWith('?');
        const trimmedSpec = isStringSpec && optional ? childSpec.slice(0, -1) : childSpec;

        const hasProp = Object.prototype.hasOwnProperty.call(value, key);
        if (!hasProp) {
          if (!optional) errs.push(`${path}.${key}: missing`);
          continue;
        }
        errs = errs.concat(
          FirebaseSchema.validateAgainstSchema((value as any)[key], trimmedSpec, `${path}.${key}`)
        );
      }
      return errs;
    }

    // Unknown schema shape
    return [];
  }

  private static validatePrimitive(value: any, spec: string, path: string): string[] {
    // Handle optional marker already stripped at caller when part of an object
    const base = spec.endsWith('?') ? spec.slice(0, -1) : spec;
    if (base === 'any') return [];

    const tokens = base.split('|').map(s => s.trim()).filter(Boolean);
    for (const t of tokens) {
      if (t === 'string' && typeof value === 'string') return [];
      if (t === 'number' && typeof value === 'number') return [];
      if (t === 'boolean' && typeof value === 'boolean') return [];
      if (t === 'null' && value === null) return [];
      // quoted literal value
      if ((t.startsWith('"') && t.endsWith('"')) || (t.startsWith('\'') && t.endsWith('\''))) {
        const lit = t.slice(1, -1);
        if (value === lit) return [];
      }
    }
    return [`${path}: expected ${spec}`];
  }

  private static validateInteractivitiesArray(arr: any[], unionSpec: any, path: string): string[] {
    // Partition base fields (string specs) vs variant specs (object specs)
    const baseSpec: any = {};
    const variants: Record<string, any> = {};
    for (const [k, v] of Object.entries(unionSpec || {})) {
      if (typeof v === 'string') baseSpec[k] = v;
      else if (v && typeof v === 'object') variants[k] = v;
    }

    let errs: string[] = [];
    if (!Array.isArray(arr)) {
      return [`${path}: expected array`];
    }
    for (let i = 0; i < arr.length; i++) {
      const item = arr[i];
      const itemPath = `${path}[${i}]`;
      // First, validate base fields
      errs = errs.concat(FirebaseSchema.validateAgainstSchema(item, baseSpec, itemPath));

      // Then, if a declared class matches a variant, validate its extra fields
      const klass = (item && typeof item === 'object') ? (item as any).class : undefined;
      if (typeof klass === 'string' && variants[klass]) {
        errs = errs.concat(
          FirebaseSchema.validateAgainstSchema(item, variants[klass], `${itemPath} (${klass})`)
        );
      }
    }
    return errs;
  }
}


// ---------- Schema builder from classes ----------

export class FirebaseSchemaBuilder {
  static buildScenarioTypeSchemaFromClasses(): any {
  return this.buildSchemaFromClass(Scenario);
  }

  static buildSchemaFromClass(cls: Function): any {
    const meta = __schemaRegistry.get(cls);
    if (!meta) return {};
    const out: any = {};
    for (const [key, spec] of Object.entries(meta.fields)) {
      out[key] = this.resolveSpec(spec);
    }
    return out;
  }

  private static resolveSpec(spec: FieldRuntimeSpec): any {
    if (typeof spec === 'string') return spec;
    if ((spec as any)['[]']) {
      const inner = (spec as any)['[]'] as FieldRuntimeSpec;
      return { '[]': this.resolveSpec(inner) };
    }
    if ((spec as any).$ref) {
      const ref = (spec as any).$ref as Function;
      return this.buildSchemaFromClass(ref);
    }
    if ((spec as any).$unionVariants) {
      const { base, variants } = (spec as any).$unionVariants as {
        base: Function; variants: Record<string, Function>;
      };
      return this.buildUnionVariants(base, variants);
    }
    // inline object maps (already in leaf specs)
    const obj = spec as any;
    const out: any = {};
    for (const [k, v] of Object.entries(obj)) {
      out[k] = this.resolveSpec(v as FieldRuntimeSpec);
    }
    return out;
  }

  private static buildUnionVariants(base: Function, variants: Record<string, Function>): any {
    const baseSchema = this.buildSchemaFromClass(base);
    const out: any = { BaseInteractivity: { ...baseSchema } };
    for (const [name, cls] of Object.entries(variants)) {
      const variantSchema = this.buildSchemaFromClass(cls);
      out.BaseInteractivity[name] = this.diffSchema(variantSchema, baseSchema);
    }
    return out;
  }

  // Remove keys present in base from variant, keep only extensions
  private static diffSchema(variant: any, base: any): any {
    if (!variant || typeof variant !== 'object') return variant;
    const out: any = {};
    for (const [k, v] of Object.entries(variant)) {
      if (!(k in base)) {
        out[k] = v;
      }
    }
    return out;
  }
}

// Bridge: allow callers to use the class-built schema without changing all sites
export namespace FirebaseSchema {
  export const scenarioTypeSchemaFromClasses = () => FirebaseSchemaBuilder.buildScenarioTypeSchemaFromClasses();
}

// ---------- internal helpers ----------
function asString(v: any, fallback = ''): string { return typeof v === 'string' ? v : fallback; }
function asBoolean(v: any, fallback = false): boolean { return typeof v === 'boolean' ? v : fallback; }
function asSyncScope(v: any, fallback: SyncScope): SyncScope {
  return v === 'global' || v === 'local' ? v : fallback;
}


// ---------------- Example scenario (typed loosely) ----------------
export const scenarioSchemaExample: any = {
  cameraConfigName: 'default',
  components: [
    { enabled: true, file: 'cone.glb', id: 'cone', isPickable: true, name: 'cone', posX: 4.6, posY: 0, posZ: 0, rotX: -1.49, rotY: 19.16, rotZ: 43.06, scale: 1, type: 'ComponentModel' },
    { enabled: true, file: 'cone.glb', id: 'cone2', isPickable: true, name: 'cone2', posX: -1, posY: 0, posZ: 4, rotX: 0, rotY: 0, rotZ: 0, scale: 1, type: 'ComponentModel' },
    { enabled: true, file: 'cone.glb', id: 'cone3', isPickable: true, name: 'cone3', posX: 0, posY: 0, posZ: 7.4, rotX: 0, rotY: 0, rotZ: 0, scale: 1, type: 'ComponentModel' },
    { enabled: true, file: 'cube.glb', id: 'cube', name: 'cube', posX: -10, posY: 0, posZ: 0, rotX: 0, rotY: 0, rotZ: 0, scale: 1, type: 'ComponentModel' },
    { enabled: true, file: 'dumpster1.glb', id: 'dumpster1', name: 'dumpster1', posX: -5, posY: 0, posZ: 15, rotX: 0, rotY: 0, rotZ: 0, scale: 1, type: 'ComponentModel' },
    { enabled: true, file: 'flag.glb', id: 'flag', name: 'flag', posX: 20, posY: 5, posZ: 20, rotX: 0, rotY: 0, rotZ: 0, scale: 1, type: 'ComponentModel' },
    { enabled: true, file: 'hydrant.glb', id: 'hydrant', name: 'hydrant', posX: 5, posY: 1, posZ: -2, rotX: -11.99, rotY: -2.04, rotZ: -86.57, scale: 1, type: 'ComponentModel' },
    { enabled: true, file: 'LowPolBush.glb', id: 'LowPolBush', name: 'Low Poly Bush', posX: 0, posY: 0, posZ: 10, rotX: 0, rotY: 0, rotZ: 0, scale: 0.5, type: 'ComponentModel' },
    { enabled: true, file: 'marker.glb', id: 'marker', name: 'marker', posX: 0, posY: 2, posZ: 0, rotX: 0, rotY: 0, rotZ: 0, scale: 1, type: 'ComponentModel' },
    { enabled: false, file: 'shed2.glb', id: 'shed2', name: 'shed', posX: -10, posY: 0, posZ: -10, rotX: 0, rotY: 0, rotZ: 0, scale: 1, type: 'ComponentModel' },
    { enabled: true, file: 'stopsign.glb', id: 'stopsign', name: 'stopsign', posX: -3, posY: 0, posZ: 5, rotX: 30, rotY: 180, rotZ: 45, scale: 0.8, type: 'ComponentModel' },
    { enabled: true, file: 'streetsignx20.glb', id: 'streetsignx20', name: 'streetsignx20', posX: 20, posY: 0, posZ: 0, rotX: 0, rotY: 180, rotZ: 0, scale: 1, type: 'ComponentModel' },
    { enabled: true, file: 'stretcher.glb', id: 'stretcher', name: 'stretcher', posX: 0, posY: 0, posZ: 0, rotX: 0, rotY: 0, rotZ: 0, scale: 1, type: 'ComponentModel' },
    { enabled: false, file: 'treea.glb', id: 'treea', name: 'treea', posX: -10, posY: 0, posZ: -10, rotX: 0, rotY: 0, rotZ: 0, scale: 1, type: 'ComponentModel' },
    { checkCollisions: true, enabled: true, file: 'Wall01Small.glb', id: 'Wall01Small', isPickable: false, name: 'Wall01Small', posX: 30, posY: 0, posZ: -30, rotX: 0, rotY: 180, rotZ: 0, scale: 1, type: 'ComponentModel' },
    { checkCollisions: true, enabled: true, file: 'wallfence.glb', id: 'wallfence', name: 'wallfence', posX: 4, posY: 0, posZ: -4, rotX: 0, rotY: 0, rotZ: 0, scale: 1, type: 'ComponentModel' }
  ],
  description: 'Testing components with the new vite project.',
  displayName: 'Development Test',
  doPerformanceLogging: true,
  ground: { fileName: 'ground.png', height: 60, maxHeight: '', subdivisions: 20, terrain: 'dddd', width: 60 },
  id: 'Development Test',
  interactivities: [
    { class: 'Grass', component: '', enabled: false, name: 'testia', patches: [ { nBlades: 30, radius: 5, xPos: -10, zPos: 5 }, { nBlades: 2500, radius: 7, xPos: 11, zPos: 2 }, { nBlades: 50, radius: 1, xPos: -11, zPos: 3 } ], seqNo: '10', type: 'button' },
    { class: 'ModalHandler', component: '', enabled: true, modalHTMLFileName: 'inviteWithUrl.html', modalId: 'inviteWithUrlModalId', name: 'testia', syncScope: 'local' },
    { class: 'Tracker', component: 'stretcher', enabled: true, name: 'testia', seqNo: 35, type: 'button' },
    { class: 'Fire', component: '', enabled: true, name: 'testiaghg', posX: 5, posY: 3, posZ: 15, seqNo: 70, type: 'buttfg' },
    { class: 'WindDirection', component: 'flag', enabled: true, seqNo: 121 },
    { class: 'ModalHandler', component: '', enabled: true, modalHTMLFileName: 'scenarioDescription.html', modalId: 'scenarioModalId', seqNo: 130, syncScope: 'global' },
    { class: 'ModalHandler', enabled: true, modalHTMLFileName: 'navigationDescription.html', modalId: 'navigationModalId', seqNo: 141, syncScope: 'global' }
  ],
  isVersion2: true,
  lessonplan: [
    { UI: { buttonText: 'invite', interactivityClass: 'ModalHandler', modalId: 'inviteWithUrlModalId', type: 'button' }, description: 'Invite learners: press “URL” button, copy the url, and paste to videoconference chat.c', id: '0', name: 'Invite', seqNo: 5 },
    { UI: { buttonText: 'Navigation', interactivityClass: 'ModalHandler', modalId: 'navigationModalId', type: 'button' }, description: 'Use WASD or Up,Down, Left, Right keybord. Use mouse to look around and mouse wheel zoom in and out', id: '1', name: 'Navigation', seqNo: 10 },
    { UI: { buttonText: 'Description', interactivityClass: 'ModalHandler', modalId: 'scenarioModalId', type: 'button' }, description: 'Gather everyone to Staging Area.  Conduct Initial assessment of site and learners. Press “Intro” button to display intro. ', id: '2', name: 'Scenario', seqNo: 20 },
    { UI: { buttonText: 'Change Direction', interactivityClass: 'WindDirection', modalId: 'None', type: 'button' }, description: 'Discuss wind direction, forecast and other conditions (sun, rain, etc) Goal: Do not stay downwind from danger', id: '3', name: 'Wind', seqNo: 30 },
    { description: 'Discuss roles.  Learners press button “Role” and select role.', id: '4', name: 'Roles', seqNo: 40 }
  ],
  modifiers: [ { class: 'PathInstancer', commands: 'ss2.5d-90m1s23r90s24r90s24r90s24', componentname: 'Wall01Small', enabled: true, seqNo: 25 } ],
  showAxis: true,
  skybox: { posy: 0, reflectionTexture: 'arid', size: 100 },
  stagingX0: -5, stagingX1: 5, stagingY0: -5, stagingY1: 5
};
